import { DropOffLocation, DropOffScheduleKey, LazyDropOffSchedule } from '../../domain/models'
import { DropOffLocationAddress, DropOffOpenCloseHour } from '../../domain/models/DropOffLocation'
import { IDropOffProvider, ISearchParams, PaginatedResult } from '../../domain/providers/DropOffProvider'
import { WoosmapFeature, WoosmapSchedule } from './woosmapSdk'

class DropOffProvider implements IDropOffProvider {
  public static toDropOffSchedule(data: WoosmapSchedule): LazyDropOffSchedule {
    const dropOffSchedule: LazyDropOffSchedule = {}
    const scheduleList = data.usual

    const keys: Array<DropOffScheduleKey> = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday'
    ]

    for (let index = 0; index < keys.length; index++) {
      const scheduleIndex = index + 1

      dropOffSchedule[keys[index]] = {
        openHours: []
      }

      if (!(scheduleIndex in scheduleList)) {
        continue
      }

      const daySchedule = scheduleList[scheduleIndex]
      if (daySchedule.length === 0) {
        continue
      }

      const openHours: DropOffOpenCloseHour[] = daySchedule.map((hour) => {
        return {
          opening: hour.start,
          closing: hour.end
        }
      })

      dropOffSchedule[keys[index]] = {
        openHours
      }
    }

    return dropOffSchedule
  }

  private static getWasteTypeIds(tags: string[]): string[] {
    return tags.filter((tag) => tag.startsWith('WT_')).map((tag) => tag.slice(3))
  }

  public static toDropOffLocation(data: WoosmapFeature): DropOffLocation {
    const properties = data.properties

    let schedule: LazyDropOffSchedule | undefined = undefined
    if (properties.opening_hours) {
      schedule = DropOffProvider.toDropOffSchedule(properties.opening_hours)
    }

    let pictures: Array<string> = []
    const wasteTypeIds: Array<string> = DropOffProvider.getWasteTypeIds(data.properties.tags)

    if (data.properties.user_properties) {
      //Extract all the <picture_X> properties to gather the pictures of the asset
      const { user_properties } = data.properties
      const regex = /^picture_\d+/
      const keys = Object.keys(user_properties)
      const picturesKeys = keys.filter((it) => regex.test(it))
      for (const key of picturesKeys) {
        pictures.push(user_properties[key])
      }
      // Photos under 'picturesUrl' key
      const customPictures: string[] = data.properties.user_properties['picturesUrl'] || []
      pictures = pictures.concat(customPictures)
    }

    let address: DropOffLocationAddress | undefined = undefined
    if (properties.address && properties.address.lines && properties.address.lines.length > 0) {
      address = {
        line: properties.address.lines.length > 0 ? properties.address.lines[0] : '',
        zipCode: properties.address.zipcode,
        countryCode: properties.address.country_code,
        city: properties.address.city,
        province: properties.user_properties?.province
      }
    }
    const isOpen = properties.open?.open_now || false

    return new DropOffLocation({
      storeId: properties.store_id,
      wasteBoxId: properties.user_properties?.wasteBoxId,
      tenantId: properties.user_properties?.tenantId,
      name: properties.name,
      tags: properties.tags,
      types: properties.types,
      pictures,
      address,
      coordinate: {
        longitude: data.geometry.coordinates[0],
        latitude: data.geometry.coordinates[1]
      },
      acceptedWasteIds: wasteTypeIds,
      contact: data.properties.contact ?? undefined,
      schedule,
      isOpenNow: isOpen,
      brandProgram: properties.user_properties?.brandProgramAffiliation ?? undefined
    })
  }

  initWoosmap(woosmapPublicKey: string, callback: () => void) {
    window.WoosmapLoader.load({
      version: '1.4',
      publicKey: woosmapPublicKey,
      loadJquery: false,
      callback
    })
  }

  fetchDropOffList(params?: Partial<ISearchParams>): Promise<PaginatedResult<DropOffLocation>> {
    const dataSource = new window.woosmap.DataSource()
    return new Promise((resolve) => {
      dataSource.searchStoresByParameters(
        new window.woosmap.search.SearchParameters({
          lat: params?.lat,
          lng: params?.lng,
          query: params?.query,
          storesByPage: params?.storesByPage || 100,
          maxDistanceFromUser: params?.radius
        }),
        (data) => {
          resolve({
            pagination: data.pagination,
            items: data.features.map((it) => DropOffProvider.toDropOffLocation(it))
          })
        }
      )
    })
  }

  public async fetchDropOffByStoreId(id: string): Promise<DropOffLocation | null> {
    const dataSource = new window.woosmap.DataSource()
    return new Promise((resolve) => {
      dataSource.searchStoresByParameters(
        new window.woosmap.search.SearchParameters({
          query: `idstore:="${id}"`
        }),
        (data) => {
          const res = data.features
          if (res.length === 0) {
            resolve(null)
          } else {
            resolve(DropOffProvider.toDropOffLocation(res[0]))
          }
        }
      )
    })
  }
}

export default DropOffProvider
